import {
  type BillingInterval,
  PLAN_DISPLAY,
  type SelfServePlanKey,
} from "@giftforward/entitlements";
import { PLAN_INTERVAL_STRINGS } from "@/constants/Plans";
import { usePlans } from "@/hooks/usePlans";
import { formatPlanCharge, selectedOffer } from "@/utils/plan-price-helper";

interface OnboardingPaymentStepProps {
  /** The paid plan picked at the plan step. */
  plan: SelfServePlanKey;
  /** Monthly or Annual, as chosen in the payment drawer. */
  interval: BillingInterval;
  onOpenDrawer: () => void;
}

/**
 * The card behind the payment drawer.
 *
 * Choosing a paid plan used to land on the team/invite step, which asked a client who had not paid
 * to fill "2 more seats" on a plan they did not have yet, and only opened the drawer once they
 * pressed Continue past it. Payment now happens here, immediately after setup, and the invite step
 * follows the charge — where its "Welcome to Good Pro!" is true.
 *
 * Closing the drawer leaves this on screen. The way to another plan, Starter included, is the
 * wizard's Back to the plan cards.
 */
export function OnboardingPaymentStep({
  plan,
  interval,
  onOpenDrawer,
}: OnboardingPaymentStepProps) {
  // The organization exists by this step, so the catalogue (GET /plans) is readable now.
  const { byKey } = usePlans();
  const offer = selectedOffer(byKey(plan)?.offers ?? [], interval);

  return (
    <div className="gfg-ob-panel-in">
      <h1 className="gfg-auth-title">Complete your {PLAN_DISPLAY[plan].name} subscription</h1>
      <p className="gfg-auth-sub">
        {offer &&
          `${formatPlanCharge(offer)} today, ${PLAN_INTERVAL_STRINGS.billed[offer.interval]}. `}
        Your organization is ready — this is the last step.
      </p>

      <button type="button" className="gfg-auth-btn" onClick={onOpenDrawer}>
        Enter payment details
      </button>
    </div>
  );
}
